/**
 * VoiceAura
 * Анимация голосового ввода: пятно света вокруг кнопки микрофона на WebGL.
 * Без зависимостей. Если WebGL недоступен, рисует мягкий ореол на Canvas 2D.
 */

export interface VoiceAuraColors {
  blue: string
  sky: string
  peri: string
  violet: string
  warm: string
}

export interface VoiceAuraOptions {
  anchor?: HTMLElement | null
  maxDpr?: number
  attack?: number
  release?: number
  bandAttack?: number
  bandRelease?: number
  floorDb?: number
  ceilDb?: number
  gate?: number
  fade?: number
  colors?: Partial<VoiceAuraColors>
}

interface ResolvedOptions {
  anchor: HTMLElement | null
  maxDpr: number
  attack: number
  release: number
  bandAttack: number
  bandRelease: number
  floorDb: number
  ceilDb: number
  gate: number
  fade: number
  colors: VoiceAuraColors
}

interface AudioState {
  ctx: AudioContext
  analyser: AnalyserNode
  source: MediaStreamAudioSourceNode | null
  td: Float32Array
  fd: Uint8Array
}

const DEFAULTS: ResolvedOptions = {
  anchor: null,
  maxDpr: 1.5,
  attack: 0.35,
  release: 0.08,
  bandAttack: 0.3,
  bandRelease: 0.07,
  floorDb: -60,
  ceilDb: -12,
  gate: 0.06,
  fade: 0.12,
  colors: {
    blue: '#2F5BEA',
    sky: '#5A94FF',
    peri: '#7A8CFF',
    violet: '#8D6CF6',
    warm: '#F89555',
  },
}

const BANDS_HZ: [number, number][] = [
  [85, 300],
  [300, 2000],
  [2000, 5500],
]

const VERT = 'attribute vec2 p; void main(){ gl_Position = vec4(p, 0.0, 1.0); }'

const FRAG = `
precision mediump float;
uniform vec2 uC;
uniform float uR;
uniform float uT;
uniform float uA;
uniform float uW;
uniform vec3 uB;
uniform vec3 cBlue; uniform vec3 cSky; uniform vec3 cPeri; uniform vec3 cViol; uniform vec3 cWarm;

float rad(float a, float t, float s, float wob) {
  float w = 0.55 * sin(3.0 * a + t * 1.25 + s)
          + 0.30 * sin(5.0 * a - t * 1.85 + s * 1.7)
          + 0.15 * sin(8.0 * a + t * 2.6 - s * 0.6);
  return 1.0 + wob * uW * w;
}
vec4 over(vec4 acc, vec3 c, float a) {
  return vec4(c * a + acc.rgb * (1.0 - a), a + acc.a * (1.0 - a));
}
void main() {
  vec2 p = (gl_FragCoord.xy - uC) / uR;
  float r = length(p);
  float a = atan(p.y, p.x);
  float t = uT;
  float grow = mix(0.82, 1.0, uA);
  float breath = 0.05 * (0.5 + 0.5 * sin(t * 1.4));
  vec4 acc = vec4(0.0);

  float R1 = grow * (1.3 + breath + 1.8 * uB.x) * rad(a, t * 0.8, 0.0, 0.06 + 0.22 * uB.x);
  float d1 = r - R1;
  float m1 = d1 < 0.0 ? 1.0 : exp(-d1 * 2.0);
  acc = over(acc, mix(cViol, cWarm, 0.5 + 0.5 * sin(a + t * 0.5)), m1 * 0.22 * uA);

  float R2 = grow * (1.18 + breath + 1.3 * uB.y) * rad(a, t, 2.1, 0.05 + 0.2 * uB.y);
  float m2 = 1.0 - smoothstep(R2 - 0.18, R2 + 0.10, r);
  acc = over(acc, mix(cPeri, cSky, 0.5 + 0.5 * sin(a * 2.0 - t * 0.7)), m2 * 0.42 * uA);

  float R3 = grow * (1.06 + breath * 0.6 + 0.75 * uB.z) * rad(a, t * 1.2, 4.2, 0.04 + 0.16 * uB.z);
  float m3 = 1.0 - smoothstep(R3 - 0.16, R3 + 0.06, r);
  acc = over(acc, mix(cBlue, cSky, clamp(0.5 + 0.4 * p.y / max(R3, 0.001), 0.0, 1.0)), m3 * 0.6 * uA);

  gl_FragColor = acc;
}`

const hexToRgb = (hex: string): [number, number, number] => {
  const n = parseInt(hex.replace('#', ''), 16)
  return [((n >> 16) & 255) / 255, ((n >> 8) & 255) / 255, (n & 255) / 255]
}

const compileShader = (
  gl: WebGLRenderingContext,
  type: number,
  src: string,
) => {
  const s = gl.createShader(type)
  if (!s) throw new Error('Failed to create shader')
  gl.shaderSource(s, src)
  gl.compileShader(s)
  if (!gl.getShaderParameter(s, gl.COMPILE_STATUS)) {
    throw new Error(gl.getShaderInfoLog(s) ?? 'Shader compile failed')
  }
  return s
}

const linkVoiceAuraProgram = (gl: WebGLRenderingContext) => {
  if (gl.isContextLost()) {
    throw new Error('WebGL context lost')
  }
  const prog = gl.createProgram()
  if (!prog) throw new Error('Failed to create program')
  gl.attachShader(prog, compileShader(gl, gl.VERTEX_SHADER, VERT))
  gl.attachShader(prog, compileShader(gl, gl.FRAGMENT_SHADER, FRAG))
  gl.linkProgram(prog)
  if (!gl.getProgramParameter(prog, gl.LINK_STATUS)) {
    throw new Error(gl.getProgramInfoLog(prog) ?? 'Program link failed')
  }
  return prog
}

const GL_OPTS: WebGLContextAttributes = {
  alpha: true,
  premultipliedAlpha: true,
  antialias: false,
}

const clamp01 = (v: number) => Math.min(1, Math.max(0, v))

const follow = (
  cur: number,
  target: number,
  dt: number,
  up: number,
  down: number,
) => {
  const a = target > cur ? up : down
  return cur + (target - cur) * (1 - Math.pow(1 - a, dt * 60))
}

const noop = () => {
  /* ignore */
}

export class VoiceAura {
  static isSupported() {
    try {
      return !!document.createElement('canvas').getContext('webgl')
    } catch {
      return false
    }
  }

  private canvas: HTMLCanvasElement
  private o: ResolvedOptions
  private reduce: boolean
  private reduceMql: MediaQueryList

  private recording = false
  private manual = false
  private target = 0
  private targetBands = [0, 0, 0]
  private manualBands: number[] | null = null
  private level = 0
  private bands = [0, 0, 0]
  private active = 0
  private t = 0
  private clock = 0

  private audio: AudioState | null = null
  private gl: WebGLRenderingContext | null = null
  private ctx2d: CanvasRenderingContext2D | null = null
  private glLost = false
  private U: Record<string, WebGLUniformLocation | null> = {}
  private center: [number, number] = [0, 0]
  private radius = 1

  private _raf = 0
  private _last = 0
  private _ro: ResizeObserver
  private _onLost: (e: Event) => void
  private _onRestored: () => void
  private _onVisibility: () => void
  private _onReduceChange: () => void

  constructor(canvas: HTMLCanvasElement, options: VoiceAuraOptions = {}) {
    this.canvas = canvas
    this.o = {
      ...DEFAULTS,
      ...options,
      colors: { ...DEFAULTS.colors, ...(options.colors ?? {}) },
    }
    this.reduceMql = window.matchMedia('(prefers-reduced-motion: reduce)')
    this.reduce = this.reduceMql.matches

    this._ensureRenderer()

    this._onLost = (e) => {
      e.preventDefault()
      this.glLost = true
    }
    this._onRestored = () => {
      this.glLost = false
      this._ensureRenderer()
      this._resize()
    }
    canvas.addEventListener('webglcontextlost', this._onLost)
    canvas.addEventListener('webglcontextrestored', this._onRestored)

    this._onVisibility = () => {
      if (document.hidden) {
        cancelAnimationFrame(this._raf)
        this._raf = 0
        return
      }
      if (this.recording || this.active > 0.003) {
        this._loop()
      }
    }
    document.addEventListener('visibilitychange', this._onVisibility)

    this._onReduceChange = () => {
      this.reduce = this.reduceMql.matches
    }
    this.reduceMql.addEventListener('change', this._onReduceChange)

    this._ro = new ResizeObserver(() => {
      this._resize()
    })
    this._ro.observe(canvas)
    if (this.o.anchor) this._ro.observe(this.o.anchor)
    this._resize()
  }

  setAnchor(anchor: HTMLElement | null) {
    if (this.o.anchor === anchor) return
    if (this.o.anchor) this._ro.unobserve(this.o.anchor)
    this.o.anchor = anchor
    if (anchor) this._ro.observe(anchor)
    this._resize()
  }

  start(stream?: MediaStream) {
    this.recording = true
    this._disconnect()
    if (stream) {
      this.manual = false
      this._connect(stream)
    } else {
      this.manual = true
    }
    this._resize()
    this._loop()
  }

  stop() {
    this.recording = false
    this._disconnect()
  }

  setLevel(level: number, bands?: number[]) {
    this.target = clamp01(level)
    this.manualBands = bands ? bands.map(clamp01) : null
  }

  destroy() {
    cancelAnimationFrame(this._raf)
    this._raf = 0
    this._ro.disconnect()
    this.canvas.removeEventListener('webglcontextlost', this._onLost)
    this.canvas.removeEventListener('webglcontextrestored', this._onRestored)
    document.removeEventListener('visibilitychange', this._onVisibility)
    this.reduceMql.removeEventListener('change', this._onReduceChange)
    this._disconnect()
    if (this.audio) {
      void this.audio.ctx.close().catch(noop)
      this.audio = null
    }
    // Не вызывать WEBGL_lose_context: React Strict Mode монтирует канвас
    // заново, и потерянный контекст ломает повторную компиляцию шейдера.
    this.gl = null
    this.ctx2d = null
  }

  private _connect(stream: MediaStream) {
    if (!this.audio) {
      const ctx = new AudioContext()
      const analyser = ctx.createAnalyser()
      analyser.fftSize = 1024
      analyser.smoothingTimeConstant = 0
      this.audio = {
        ctx,
        analyser,
        source: null,
        td: new Float32Array(analyser.fftSize),
        fd: new Uint8Array(analyser.frequencyBinCount),
      }
    }
    this.audio.source = this.audio.ctx.createMediaStreamSource(stream)
    this.audio.source.connect(this.audio.analyser)
    void this.audio.ctx.resume().catch(noop)
  }

  private _disconnect() {
    if (this.audio?.source) {
      this.audio.source.disconnect()
      this.audio.source = null
    }
  }

  private _readAudio(out: number[]) {
    const audio = this.audio
    if (!audio) return 0
    const { analyser, td, fd, ctx } = audio
    analyser.getFloatTimeDomainData(td)
    let sum = 0
    for (const sample of td) {
      sum += sample * sample
    }
    const db = 20 * Math.log10(Math.sqrt(sum / td.length) + 1e-8)
    const { floorDb, ceilDb, gate } = this.o
    let level = clamp01((db - floorDb) / (ceilDb - floorDb))
    level = Math.max(0, (level - gate) / (1 - gate))

    analyser.getByteFrequencyData(fd)
    const binHz = ctx.sampleRate / analyser.fftSize
    const e = BANDS_HZ.map(([lo, hi]) => {
      const a = Math.max(1, Math.round(lo / binHz))
      const b = Math.min(fd.length - 1, Math.round(hi / binHz))
      let s = 0
      for (let i = a; i <= b; i++) s += fd[i]
      return s / Math.max(1, b - a + 1)
    })
    const m = Math.max(e[0], e[1], e[2]) + 1e-6
    for (let i = 0; i < 3; i++) {
      out[i] = level * (0.55 + 0.45 * (e[i] / m))
    }
    return level
  }

  private _ensureRenderer() {
    if (this._initGL()) return
    this.gl = null
    this.ctx2d = this.canvas.getContext('2d')
  }

  private _initGL() {
    try {
      const gl = this.canvas.getContext('webgl', GL_OPTS)
      if (!gl) return false
      if (gl.isContextLost()) {
        gl.getExtension('WEBGL_lose_context')?.restoreContext()
        return false
      }

      const prog = linkVoiceAuraProgram(gl)
      gl.useProgram(prog)

      const buf = gl.createBuffer()
      gl.bindBuffer(gl.ARRAY_BUFFER, buf)
      gl.bufferData(
        gl.ARRAY_BUFFER,
        new Float32Array([-1, -1, 3, -1, -1, 3]),
        gl.STATIC_DRAW,
      )
      const loc = gl.getAttribLocation(prog, 'p')
      gl.enableVertexAttribArray(loc)
      gl.vertexAttribPointer(loc, 2, gl.FLOAT, false, 0, 0)

      const U: Record<string, WebGLUniformLocation | null> = {}
      ;[
        'uC',
        'uR',
        'uT',
        'uA',
        'uW',
        'uB',
        'cBlue',
        'cSky',
        'cPeri',
        'cViol',
        'cWarm',
      ].forEach((n) => {
        U[n] = gl.getUniformLocation(prog, n)
      })
      const c = this.o.colors
      gl.uniform3fv(U.cBlue, hexToRgb(c.blue))
      gl.uniform3fv(U.cSky, hexToRgb(c.sky))
      gl.uniform3fv(U.cPeri, hexToRgb(c.peri))
      gl.uniform3fv(U.cViol, hexToRgb(c.violet))
      gl.uniform3fv(U.cWarm, hexToRgb(c.warm))

      gl.disable(gl.DEPTH_TEST)
      gl.enable(gl.BLEND)
      gl.blendFunc(gl.ONE, gl.ONE_MINUS_SRC_ALPHA)
      gl.clearColor(0, 0, 0, 0)

      this.gl = gl
      this.U = U
      return true
    } catch (e) {
      console.warn('VoiceAura: WebGL не запустился, переходим на Canvas 2D', e)
      this.gl = null
      return false
    }
  }

  private _resize() {
    const dpr = Math.min(window.devicePixelRatio || 1, this.o.maxDpr)
    const cr = this.canvas.getBoundingClientRect()
    this.canvas.width = Math.max(1, Math.round(cr.width * dpr))
    this.canvas.height = Math.max(1, Math.round(cr.height * dpr))
    if (this.o.anchor) {
      const br = this.o.anchor.getBoundingClientRect()
      this.center = [
        (br.left + br.width / 2 - cr.left) * dpr,
        (cr.bottom - (br.top + br.height / 2)) * dpr,
      ]
      this.radius = Math.max(1, (br.width / 2) * dpr)
    } else {
      this.center = [this.canvas.width / 2, this.canvas.height / 2]
      this.radius = Math.min(this.canvas.width, this.canvas.height) / 6
    }
    if (this.gl && !this.glLost) {
      this.gl.viewport(0, 0, this.canvas.width, this.canvas.height)
    }
    if (!this._raf) this._draw()
  }

  private _loop() {
    if (this._raf || document.hidden) return
    this._last = performance.now()
    this._raf = requestAnimationFrame((now) => {
      this._frame(now)
    })
  }

  private _updateTargets() {
    if (!this.recording) {
      this.target = 0
      this.targetBands = [0, 0, 0]
      return
    }
    if (this.audio?.source) {
      this.target = this._readAudio(this.targetBands)
      return
    }
    if (!this.manual) return
    if (this.manualBands) {
      this.targetBands = this.manualBands.slice()
      return
    }
    for (let i = 0; i < 3; i++) {
      this.targetBands[i] =
        this.target *
        (0.7 + 0.3 * Math.sin(this.clock * (1.7 + i * 0.9) + i * 2))
    }
  }

  private _smooth(dt: number) {
    const o = this.o
    this.level = follow(this.level, this.target, dt, o.attack, o.release)
    for (let i = 0; i < 3; i++) {
      this.bands[i] = follow(
        this.bands[i] ?? 0,
        this.targetBands[i] ?? 0,
        dt,
        o.bandAttack,
        o.bandRelease,
      )
    }
    this.active = follow(
      this.active,
      this.recording ? 1 : 0,
      dt,
      o.fade,
      o.fade,
    )
    this.t += dt * (0.7 + 2.0 * this.level) * (this.reduce ? 0.3 : 1)
  }

  private _frame(now: number) {
    if (document.hidden) {
      this._raf = 0
      return
    }

    const dt = Math.min((now - this._last) / 1000, 0.1)
    this._last = now
    this.clock += dt
    this._updateTargets()
    this._smooth(dt)
    this._draw()

    if (!this.recording && this.active < 0.003) {
      this.active = 0
      this._draw()
      this._raf = 0
      return
    }
    this._raf = requestAnimationFrame((next) => {
      this._frame(next)
    })
  }

  private _draw() {
    if (this.gl) {
      if (this.glLost) return
      const gl = this.gl
      const U = this.U
      gl.clearColor(0, 0, 0, 0)
      gl.clear(gl.COLOR_BUFFER_BIT)
      if (this.active < 0.003) return
      gl.uniform2f(U.uC, this.center[0], this.center[1])
      gl.uniform1f(U.uR, this.radius)
      gl.uniform1f(U.uT, this.t)
      gl.uniform1f(U.uA, this.active)
      gl.uniform1f(U.uW, this.reduce ? 0.4 : 1)
      gl.uniform3f(
        U.uB,
        this.bands[0] ?? 0,
        this.bands[1] ?? 0,
        this.bands[2] ?? 0,
      )
      gl.drawArrays(gl.TRIANGLES, 0, 3)
    } else if (this.ctx2d) {
      this._draw2d()
    }
  }

  private _draw2d() {
    const c = this.ctx2d
    if (!c) return
    c.clearRect(0, 0, this.canvas.width, this.canvas.height)
    if (this.active < 0.003) return
    const [x, yFromBottom] = this.center
    const y = this.canvas.height - yFromBottom
    const grow = 0.82 + 0.18 * this.active
    const layers = [
      {
        color: this.o.colors.peri,
        alpha: 0.3,
        size: 1.3 + 1.8 * (this.bands[0] ?? 0),
      },
      {
        color: this.o.colors.blue,
        alpha: 0.32,
        size: 1.1 + 0.9 * (this.bands[2] ?? 0),
      },
    ]
    for (const l of layers) {
      const r = this.radius * l.size * grow
      const rgb = hexToRgb(l.color).map((v) => Math.round(v * 255))
      const cr = rgb[0] ?? 0
      const cg = rgb[1] ?? 0
      const cb = rgb[2] ?? 0
      const g = c.createRadialGradient(x, y, 0, x, y, r)
      g.addColorStop(0, `rgba(${cr},${cg},${cb},${l.alpha * this.active})`)
      g.addColorStop(
        0.6,
        `rgba(${cr},${cg},${cb},${l.alpha * 0.45 * this.active})`,
      )
      g.addColorStop(1, `rgba(${cr},${cg},${cb},0)`)
      c.fillStyle = g
      c.beginPath()
      c.arc(x, y, r, 0, Math.PI * 2)
      c.fill()
    }
  }
}
